import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 4rem 1.5rem;
`;

const PageTitle = styled(motion.h1)`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 2rem;
  text-align: center;
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 1.25rem;
  color: ${({ theme }) => theme.colors.primary};
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.gray};
`;

const List = styled.ul`
  margin-bottom: 1.5rem;
  padding-left: 1.5rem;
`;

const ListItem = styled.li`
  margin-bottom: 0.75rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.gray};
`;

const LastUpdated = styled.p`
  font-style: italic;
  margin-bottom: 2rem;
  color: ${({ theme }) => theme.colors.grayLight};
`;

const PrivacyPolicyPage: React.FC = () => {
  return (
    <PageContainer>
      <PageTitle
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        Privacy Policy
      </PageTitle>
      
      <LastUpdated>Last Updated: July 8, 2025</LastUpdated>
      
      <Section>
        <Paragraph>
          At FylePrep, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>Information We Collect</SectionTitle>
        <Paragraph>
          We collect information that you provide directly to us when you:
        </Paragraph>
        <List>
          <ListItem>Register for our service or create an account</ListItem>
          <ListItem>Fill out forms or submit documents through our platform</ListItem>
          <ListItem>Communicate with us via email, phone, or other methods</ListItem>
          <ListItem>Participate in surveys, promotions, or other activities</ListItem>
        </List>
        <Paragraph>
          This information may include your name, email address, phone number, company information, billing details, and any other information you choose to provide.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>How We Use Your Information</SectionTitle>
        <Paragraph>
          We may use the information we collect for various purposes, including to:
        </Paragraph>
        <List>
          <ListItem>Provide, maintain, and improve our services</ListItem>
          <ListItem>Process transactions and send related information</ListItem>
          <ListItem>Send administrative messages, updates, and security alerts</ListItem>
          <ListItem>Respond to your comments, questions, and requests</ListItem>
          <ListItem>Monitor and analyze trends, usage, and activities</ListItem>
          <ListItem>Personalize and improve your experience</ListItem>
          <ListItem>Comply with legal obligations</ListItem>
        </List>
      </Section>
      
      <Section>
        <SectionTitle>Information Sharing and Disclosure</SectionTitle>
        <Paragraph>
          <strong>We do not sell, rent, or share your personal information with third parties for marketing purposes.</strong> Your privacy is our priority, and we are committed to maintaining the confidentiality of your data.
        </Paragraph>
        <Paragraph>
          We may only share your information in the following limited situations:
        </Paragraph>
        <List>
          <ListItem>With trusted service providers who are contractually obligated to maintain the confidentiality and security of your data, and only to the extent necessary to perform specific services for FylePrep</ListItem>
          <ListItem>In response to a legal request (such as a subpoena or court order) if we believe disclosure is required by law</ListItem>
          <ListItem>If necessary to protect the vital interests of a person, to enforce our Terms of Service, or to address fraud or security issues</ListItem>
          <ListItem>In the event of a business transaction such as a merger or acquisition, but only with contractual protections for your data</ListItem>
          <ListItem>With your explicit consent or at your specific direction</ListItem>
        </List>
        <Paragraph>
          Even in these limited circumstances, we share only the minimum information necessary and take steps to ensure your data remains protected.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>Data Security</SectionTitle>
        <Paragraph>
          <strong>Protecting your data is our highest priority.</strong> We implement industry-standard security measures to safeguard your personal information, including:  
        </Paragraph>
        <List>
          <ListItem>Encryption of sensitive data both in transit and at rest using modern encryption protocols</ListItem>
          <ListItem>Secure access controls and authentication mechanisms to prevent unauthorized access</ListItem>
          <ListItem>Regular security assessments and vulnerability testing of our systems</ListItem>
          <ListItem>Employee training on data protection and security best practices</ListItem>
          <ListItem>Physical security measures for our server infrastructure</ListItem>
        </List>
        <Paragraph>
          While we implement these and other appropriate safeguards, no internet transmission or electronic storage system can be guaranteed to be 100% secure. If you have reason to believe that your interaction with us is no longer secure, please immediately notify us at security@fyleprep.com.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>Data Minimization and Retention</SectionTitle>
        <Paragraph>
          We follow the principles of data minimization and purpose limitation. We only collect and retain the personal information that is necessary to fulfill the purposes outlined in this Privacy Policy.
        </Paragraph>
        <Paragraph>
          We retain your personal information only for as long as necessary to provide you with our services and for legitimate business purposes, such as maintaining your account, complying with legal obligations, resolving disputes, and enforcing our agreements. When your data is no longer required for these purposes, we securely delete or anonymize it.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>Your Choices and Rights</SectionTitle>
        <Paragraph>
          You have several rights regarding your personal information:
        </Paragraph>
        <List>
          <ListItem><strong>Access and Update:</strong> You may access, update, or correct your personal information at any time by logging into your account or contacting us</ListItem>
          <ListItem><strong>Deletion:</strong> You may request deletion of your personal information, subject to certain exceptions provided by law</ListItem>
          <ListItem><strong>Communication Preferences:</strong> You may opt out of receiving promotional communications by following the instructions in those communications</ListItem>
          <ListItem><strong>Data Portability:</strong> You may request a copy of your personal information in a structured, commonly used format</ListItem>
        </List>
        <Paragraph>
          To exercise any of these rights, please contact us at privacy@fyleprep.com. We will respond to your request within a reasonable timeframe.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>Changes to this Privacy Policy</SectionTitle>
        <Paragraph>
          We may change this privacy policy from time to time. If we make changes, we will notify you by revising the date at the top of the policy and, in some cases, we may provide you with additional notice.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>Contact Us</SectionTitle>
        <Paragraph>
          If you have any questions about this privacy policy, please contact us at: privacy@fyleprep.com
        </Paragraph>
      </Section>
    </PageContainer>
  );
};

export default PrivacyPolicyPage;
